/**
 * Visualizador dos autos do processo via adapters MNI/eproc/e-SAJ.
 *
 * Modal full-screen mostra:
 *   - Cabeçalho do processo (consultarProcesso)
 *   - Lista cronológica de movimentos com peças anexas
 *   - Visualização inline de peças (PDF) com link de download
 *
 * Quando o tribunal não suporta integração (e.g. e-SAJ TJSP), o componente
 * detecta via /process-systems/_capabilities/{cnj} e oferece deep-link
 * para o portal correspondente.
 */

import { Badge, Button, Spinner } from '@axioma/ui';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  ShieldAlert,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { api } from '../lib/api.js';

interface Capabilities {
  tribunal: string;
  system: 'PJE_MNI' | 'EPROC' | 'ESAJ' | 'STF_PETI' | 'PROJUDI';
  capabilities: {
    consultarProcesso: boolean;
    listarMovimentos: boolean;
    baixarDocumento: boolean;
    peticionar: boolean;
  };
  certConfigured: boolean;
  kekConfigured: boolean;
}

interface ProcessoDetalhe {
  cnjNumber: string;
  cnjMasked: string;
  tribunal: string;
  orgaoJulgador: string;
  classe: { codigo?: number; nome: string };
  assuntos: Array<{ codigo?: number; nome: string }>;
  partes: Array<{
    polo: 'ATIVO' | 'PASSIVO' | 'INTERESSADO' | 'OUTROS';
    nome: string;
    cpfCnpj?: string;
    advogados?: Array<{ nome: string; oab?: string; uf?: string }>;
  }>;
  valorCausa?: number;
  dataAjuizamento?: string;
  segredoJustica: boolean;
}

interface Movimento {
  externalId: string;
  sequencia?: number;
  codigoTpu?: number;
  texto: string;
  dataHora: string;
  documentos?: Array<{
    externalId: string;
    tipo?: string;
    nome?: string;
    mimeType?: string;
    size?: number;
  }>;
}

export function AutosViewer({
  cnj,
  cnjMasked,
  onClose,
}: {
  cnj: string;
  cnjMasked: string;
  onClose: () => void;
}): JSX.Element {
  const [selectedDoc, setSelectedDoc] = useState<{ id: string; nome: string } | null>(null);

  const caps = useQuery({
    queryKey: ['process-systems', 'capabilities', cnj],
    queryFn: async () => {
      const { data } = await api.get<Capabilities>(`/process-systems/_capabilities/${cnj}`);
      return data;
    },
  });

  const details = useQuery({
    queryKey: ['process-systems', 'details', cnj],
    queryFn: async () => {
      const { data } = await api.get<{ ok: boolean; details: ProcessoDetalhe }>(
        `/process-systems/${cnj}/details`,
      );
      return data.details;
    },
    enabled: caps.data?.capabilities.consultarProcesso === true && caps.data.certConfigured,
    retry: false,
  });

  const movements = useQuery({
    queryKey: ['process-systems', 'movements', cnj],
    queryFn: async () => {
      const { data } = await api.get<{ ok: boolean; movements: Movimento[] }>(
        `/process-systems/${cnj}/movements?limit=200`,
      );
      return data.movements;
    },
    enabled: caps.data?.capabilities.listarMovimentos === true && caps.data.certConfigured,
    retry: false,
  });

  const documentUrl = (docId: string): string =>
    `${api.defaults.baseURL ?? ''}/process-systems/${cnj}/documents/${docId}`;

  return (
    <div className="fixed inset-0 z-ax-modal flex items-stretch bg-black/70 backdrop-blur-sm">
      <div className="flex flex-1 flex-col bg-ax-obsidian">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ax-line bg-ax-graphite px-6 py-3">
          <div className="min-w-0 flex-1">
            <p className="ax-eyebrow text-ax-text-tertiary">Autos do processo</p>
            <h2 className="font-display text-lg tracking-tight text-ax-text-primary">
              {cnjMasked}
            </h2>
          </div>
          {caps.data && (
            <div className="flex items-center gap-2">
              <Badge variant="neutral" uppercase>
                {caps.data.tribunal}
              </Badge>
              <Badge variant="neutral" uppercase>
                {caps.data.system.replace('_', ' ')}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RefreshCw size={14} />}
                onClick={() => {
                  void details.refetch();
                  void movements.refetch();
                }}
              >
                Atualizar
              </Button>
            </div>
          )}
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="ml-3 rounded-ax-sm p-2 text-ax-text-tertiary hover:bg-ax-night hover:text-ax-text-primary"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {caps.isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner size="lg" variant="aurora" />
          </div>
        ) : !caps.data ? (
          <ErrorPanel msg="Falha ao carregar capabilities do tribunal." />
        ) : !caps.data.kekConfigured ? (
          <ErrorPanel
            icon={<ShieldAlert size={28} />}
            title="Cofre indisponível"
            msg="O servidor não tem KEK configurada. Contate o administrador."
          />
        ) : !caps.data.certConfigured ? (
          <ErrorPanel
            icon={<ShieldAlert size={28} />}
            title="Sem certificado"
            msg="Suba seu certificado ICP-Brasil A1 em /perfil para acessar os autos."
            cta={
              <Button
                variant="signature"
                size="md"
                onClick={() => (window.location.href = '/perfil')}
              >
                Ir para Perfil
              </Button>
            }
          />
        ) : !caps.data.capabilities.consultarProcesso ? (
          <UnsupportedPanel caps={caps.data} cnjMasked={cnjMasked} />
        ) : (
          <div className="flex flex-1 overflow-hidden">
            {/* Left: cabeçalho + movimentos */}
            <div className="w-[480px] flex-shrink-0 overflow-y-auto border-r border-ax-line">
              {details.isLoading ? (
                <div className="flex items-center justify-center p-12">
                  <Spinner size="md" variant="aurora" />
                </div>
              ) : details.isError ? (
                <ErrorPanel msg={(details.error as Error).message} />
              ) : details.data ? (
                <ProcessHeader d={details.data} />
              ) : null}

              <div className="border-t border-ax-line">
                <div className="border-b border-ax-line px-5 py-3">
                  <p className="ax-eyebrow text-ax-text-tertiary">
                    Movimentos {movements.data ? `(${movements.data.length})` : ''}
                  </p>
                </div>
                {movements.isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Spinner size="sm" variant="aurora" />
                  </div>
                ) : movements.isError ? (
                  <ErrorPanel msg={(movements.error as Error).message} />
                ) : (
                  <ul className="divide-y divide-ax-line">
                    {(movements.data ?? []).map((m) => (
                      <MovimentoRow
                        key={m.externalId}
                        m={m}
                        onSelectDoc={(d) =>
                          setSelectedDoc({ id: d.externalId, nome: d.nome ?? d.externalId })
                        }
                      />
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Right: visualização do documento selecionado */}
            <div className="flex flex-1 flex-col bg-ax-night">
              {selectedDoc ? (
                <>
                  <div className="flex items-center justify-between border-b border-ax-line bg-ax-graphite px-5 py-3">
                    <p className="truncate font-display text-sm text-ax-text-primary">
                      {selectedDoc.nome}
                    </p>
                    <a
                      href={documentUrl(selectedDoc.id)}
                      download
                      className="inline-flex items-center gap-1.5 rounded-ax-sm border border-ax-line bg-ax-night px-2 py-1 font-mono text-[11px] uppercase tracking-wider text-ax-text-secondary hover:bg-ax-graphite"
                    >
                      <Download size={12} />
                      Baixar
                    </a>
                  </div>
                  <iframe
                    src={documentUrl(selectedDoc.id)}
                    title={selectedDoc.nome}
                    className="flex-1 bg-white"
                  />
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center text-ax-text-tertiary">
                  <p className="font-mono text-[11px] uppercase tracking-wider">
                    Selecione uma peça à esquerda
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProcessHeader({ d }: { d: ProcessoDetalhe }): JSX.Element {
  return (
    <div className="space-y-3 px-5 py-4">
      <div>
        <p className="ax-eyebrow text-ax-text-tertiary">Órgão julgador</p>
        <p className="ax-body text-ax-text-primary">{d.orgaoJulgador || '—'}</p>
      </div>
      <div>
        <p className="ax-eyebrow text-ax-text-tertiary">Classe</p>
        <p className="ax-body text-ax-text-primary">
          {d.classe.nome}
          {d.classe.codigo && (
            <span className="ml-1 font-mono text-[11px] text-ax-text-tertiary">
              ({String(d.classe.codigo)})
            </span>
          )}
        </p>
      </div>
      {d.assuntos.length > 0 && (
        <div>
          <p className="ax-eyebrow text-ax-text-tertiary">Assuntos</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {d.assuntos.map((a, i) => (
              <Badge key={i} variant="neutral" uppercase>
                {a.nome}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {d.valorCausa !== undefined && (
        <div>
          <p className="ax-eyebrow text-ax-text-tertiary">Valor da causa</p>
          <p className="font-mono text-sm text-ax-text-primary">
            R$ {d.valorCausa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
        </div>
      )}
      <div>
        <p className="ax-eyebrow text-ax-text-tertiary">Partes ({d.partes.length})</p>
        <ul className="mt-1 space-y-1.5">
          {d.partes.map((p, i) => (
            <li key={i} className="rounded-ax-sm border border-ax-line bg-ax-night px-2.5 py-1.5">
              <p className="font-display text-sm text-ax-text-primary">{p.nome}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-ax-text-tertiary">
                {p.polo}
                {p.cpfCnpj ? ` · ${p.cpfCnpj}` : ''}
              </p>
              {p.advogados && p.advogados.length > 0 && (
                <p className="mt-0.5 font-mono text-[10px] text-ax-text-tertiary">
                  {p.advogados
                    .map((a) => `${a.nome}${a.oab ? ` (OAB ${a.oab}/${a.uf ?? ''})` : ''}`)
                    .join(' · ')}
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MovimentoRow({
  m,
  onSelectDoc,
}: {
  m: Movimento;
  onSelectDoc: (d: NonNullable<Movimento['documentos']>[number]) => void;
}): JSX.Element {
  return (
    <li className="px-5 py-3">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex-shrink-0 text-ax-text-tertiary">
          <FileText size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-wider text-ax-text-tertiary">
            {new Date(m.dataHora).toLocaleString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
          <p className="ax-body-sm mt-0.5 text-ax-text-primary">{m.texto}</p>
          {m.documentos && m.documentos.length > 0 && (
            <ul className="mt-2 space-y-1">
              {m.documentos.map((d) => (
                <li key={d.externalId}>
                  <button
                    type="button"
                    onClick={() => onSelectDoc(d)}
                    className="inline-flex w-full items-center gap-1.5 rounded-ax-sm border border-ax-line bg-ax-night px-2 py-1 text-left font-mono text-[11px] text-ax-text-secondary hover:border-ax-aurora hover:text-ax-aurora"
                  >
                    <FileText size={11} />
                    <span className="truncate">{d.nome ?? d.tipo ?? d.externalId}</span>
                    {d.size && (
                      <span className="ml-auto text-[10px] text-ax-text-tertiary">
                        {(d.size / 1024).toFixed(0)} KB
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
}

function ErrorPanel({
  icon,
  title,
  msg,
  cta,
}: {
  icon?: React.ReactNode;
  title?: string;
  msg: string;
  cta?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-center px-8 py-12">
      <div className="max-w-md text-center">
        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-ax-critical bg-[rgba(255,82,82,0.05)] text-ax-critical">
          {icon ?? <AlertTriangle size={18} />}
        </div>
        {title && <h3 className="font-display text-base text-ax-text-primary">{title}</h3>}
        <p className="ax-body-sm mt-1 text-ax-text-secondary">{msg}</p>
        {cta && <div className="mt-4">{cta}</div>}
      </div>
    </div>
  );
}

function UnsupportedPanel({
  caps,
  cnjMasked,
}: {
  caps: Capabilities;
  cnjMasked: string;
}): JSX.Element {
  // Para e-SAJ TJSP, oferece deep-link para o portal
  const portalUrl =
    caps.system === 'ESAJ'
      ? caps.tribunal === 'TJSP'
        ? `https://esaj.tjsp.jus.br/cpopg/show.do?processo.numero=${encodeURIComponent(cnjMasked)}`
        : caps.tribunal === 'TJBA'
          ? `https://esaj.tjba.jus.br/cpopg/show.do?processo.numero=${encodeURIComponent(cnjMasked)}`
          : undefined
      : undefined;

  return (
    <div className="flex flex-1 items-center justify-center px-8 py-12">
      <div className="max-w-lg text-center">
        <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.08)] text-ax-aurora">
          <AlertTriangle size={20} />
        </div>
        <h3 className="font-display text-lg text-ax-text-primary">
          Integração com {caps.tribunal} ainda não disponível
        </h3>
        <p className="ax-body-sm mt-2 text-ax-text-secondary">
          O sistema {caps.system.replace('_', ' ')} usado pelo {caps.tribunal} é proprietário e
          requer integração adicional. Por enquanto, abra os autos no portal oficial — o
          monitoramento de movimentos via DataJud + DJEN continua funcionando normalmente.
        </p>
        {portalUrl && (
          <div className="mt-5">
            <a
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-ax-md border border-ax-aurora bg-[rgba(229,165,71,0.08)] px-4 py-2 font-display text-sm text-ax-aurora hover:bg-[rgba(229,165,71,0.15)]"
            >
              <ExternalLink size={14} />
              Abrir no portal {caps.tribunal}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
